import React, { useState } from 'react';
import { Box, Button, Dialog, Typography, alpha } from '@mui/material';
import { tokens } from '../../theme/tokens';
import { useShellTranslation } from '../navigation/useShellTranslation';
import { APP_UPDATE_ATTR, useAppUpdate } from './UpdateContext';

const { ink, accent, signal, surface, radius, shadow } = tokens;

const buttonSx = {
  height: 32,
  minWidth: 0,
  borderRadius: `${radius.control}px`,
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
} as const;

/**
 * Blocking dialog for a `required` update: this build can no longer talk to the
 * server. No close button, Escape and backdrop clicks do nothing. It reloads on
 * its own after 30s, unless there are unsaved edits, in which case the countdown
 * pauses and the person chooses Discard or Save and refresh.
 */
export const ForcedRefreshDialog: React.FC = () => {
  const { t } = useShellTranslation();
  const { urgency, countdown, unsaved, saveError, refresh, saveAndRefresh, discardAndRefresh } =
    useAppUpdate();
  const [saving, setSaving] = useState(false);

  if (urgency !== 'required') return null;

  const onSave = async () => {
    setSaving(true);
    try {
      await saveAndRefresh();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      disableEscapeKeyDown
      onClose={() => undefined}
      aria-labelledby="app-update-required-title"
      aria-describedby="app-update-required-body"
      slotProps={{
        root: { [APP_UPDATE_ATTR]: '', sx: { zIndex: 1500 } } as Record<string, unknown>,
        backdrop: { sx: { background: alpha(ink.primary, 0.42) } },
        paper: {
          sx: {
            width: 400,
            maxWidth: 'calc(100% - 32px)',
            m: 2,
            p: '22px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            background: surface.card,
            borderRadius: `${radius.panel}px`,
            boxShadow: shadow.overlay,
          },
        },
      }}
    >
      <Typography
        id="app-update-required-title"
        component="h2"
        sx={{ fontSize: '16px', fontWeight: 700, color: ink.primary }}
      >
        {t('shell.update.required.title')}
      </Typography>
      <Typography
        id="app-update-required-body"
        sx={{ fontSize: '13px', lineHeight: 1.5, color: ink.bodyAlt }}
      >
        {unsaved ? t('shell.update.required.unsaved') : t('shell.update.required.body')}
      </Typography>
      {unsaved && saveError && (
        <Typography
          role="alert"
          sx={{ fontSize: '12.5px', fontWeight: 600, color: signal.problem.ink }}
        >
          {t('shell.update.required.saveFailed')}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          pt: '6px',
        }}
      >
        {unsaved ? (
          <>
            <Button
              onClick={discardAndRefresh}
              disabled={saving}
              sx={{ ...buttonSx, px: '12px', color: ink.bodyAlt, '&:hover': { background: surface.tint } }}
            >
              {t('shell.update.required.discard')}
            </Button>
            <Button
              variant="contained"
              onClick={() => void onSave()}
              disabled={saving}
              sx={{
                ...buttonSx,
                px: '14px',
                background: accent.main,
                color: ink.inverse,
                '&:hover': { background: accent.hover, boxShadow: 'none' },
              }}
            >
              {t('shell.update.required.saveAndRefresh')}
            </Button>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: '12px', color: ink.muted }}>
              {t('shell.update.required.countdown', { seconds: countdown })}
            </Typography>
            <Button
              variant="contained"
              onClick={refresh}
              sx={{
                ...buttonSx,
                px: '14px',
                background: accent.main,
                color: ink.inverse,
                '&:hover': { background: accent.hover, boxShadow: 'none' },
              }}
            >
              {t('shell.update.refreshNow')}
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};
